import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined, EditOutlined } from '@ant-design/icons';
import { caseAPI } from '../services/api';
import { useResponsive } from '../contexts/ResponsiveContext';
import CalculationHistory from '../components/CalculationHistory';
import TDMOptimizePanel from '../components/TDMOptimizePanel';
import ReportPreview from '../components/ReportPreview';
import { getDrugLabel } from '../constants/drugs';
import type { CalculationRecord, Case, DoseResult } from '../types';
import { normalizeScrUnit, SCR_UNIT_OPTIONS } from '../utils/clinicalUnits';

const { Title, Text } = Typography;

const CaseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isMobileShell } = useResponsive();
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reportResult, setReportResult] = useState<DoseResult | null>(null);
  const [tdmReplayDraft, setTdmReplayDraft] = useState<Record<string, any> | null>(null);
  const [editForm] = Form.useForm();
  const reportRef = useRef<HTMLDivElement>(null);

  const handleLatestCalculationLoaded = useCallback((record: CalculationRecord | null) => {
    if (!record || record.calculation_type !== 'optimize') {
      setTdmReplayDraft(null);
      return;
    }
    const results = record.results as Record<string, any>;
    const input = record.patient_data?.optimize_input || {};
    setTdmReplayDraft({
      source: 'case-history',
      sourceLabel: '病例原始录入',
      calculationId: record.id,
      modelId: input.model_id || record.model_id,
      selectedMethod: input.target_type || results.primary_method || results.selected_method,
      administrationMode: input.administration_mode || results.administration_mode,
      dosingRegimens: input.dosing_regimens || results.regimen_history || [],
      tdmSamples: input.tdm_data || record.tdm_data || [],
      targetValues: {
        target_auc: input.target_auc,
        target_value: input.target_value,
        mic: input.mic,
      },
    });
  }, []);

  const loadCase = async (caseId: string) => {
    try {
      setLoading(true);
      setError('');
      const data = await caseAPI.get(caseId);
      setCaseData(data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || '病例加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadCase(id);
    }
  }, [id]);

  const openEdit = () => {
    if (!caseData) return;
    const cov = caseData.covariates || {};
    editForm.setFieldsValue({
      diagnosis: caseData.diagnosis,
      weight: cov.weight,
      age_pma: cov.age_pma,
      age_pna: cov.age_pna,
      gestational_age: cov.gestational_age,
      age_years: cov.age_years,
      height: cov.height,
      scr: cov.scr,
      scr_unit: normalizeScrUnit(cov.scr_unit || 'mg/dL'),
      albumin: cov.albumin,
      alt: cov.alt,
      ast: cov.ast,
      ventilation: cov.ventilation || false,
      inotropes: cov.inotropes || false,
      ecmo: cov.ecmo || false,
      ibuprofen: cov.ibuprofen || false,
    });
    setEditOpen(true);
  };

  const handleSave = async () => {
    if (!id) return;
    try {
      const values = await editForm.validateFields();
      setSaving(true);
      const { diagnosis, ...covariateFields } = values;
      const covariates: Record<string, any> = {};
      Object.entries(covariateFields).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          covariates[key] = value;
        }
      });
      if (covariates.scr_unit) {
        covariates.scr_unit = normalizeScrUnit(covariates.scr_unit);
      }
      await caseAPI.update(id, { diagnosis, covariates });
      message.success('患者信息已更新，后续计算将使用最新数据');
      setEditOpen(false);
      loadCase(id);
    } catch {
      message.error('更新失败');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text>正在加载病例数据...</Text>
        </div>
      </div>
    );
  }

  if (error || !caseData) {
    return (
      <div>
        {!isMobileShell ? (
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/cases')}
            style={{ marginBottom: 16, paddingLeft: 0 }}
          >
            返回病例列表
          </Button>
        ) : null}
        <Alert
          type="error"
          message="病例加载失败"
          description={error || '未找到该病例'}
          showIcon
        />
      </div>
    );
  }

  const { covariates, derived_metrics } = caseData;

  const editFormContent = (
    <div style={{ paddingBottom: isMobileShell ? 24 : 0 }}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="修改患者信息仅影响后续剂量计算，已生成的报告正文不会被回写。"
      />
      <Form form={editForm} layout="vertical">
        <Form.Item name="diagnosis" label="诊断">
          <Input />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item name="weight" label="体重 (kg)" rules={[{ required: true, message: '请输入体重' }]}>
              <InputNumber min={0.1} max={300} step={0.1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="gestational_age" label="胎龄 (周)">
              <InputNumber min={20} max={45} step={0.1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="age_pna" label="PNA (天)">
              <InputNumber min={0} max={36500} step={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item name="age_pma" label="校正胎龄 (周)">
              <InputNumber min={20} max={100} step={0.1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="age_years" label="年龄 (岁)">
              <InputNumber min={0} max={120} step={0.1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="height" label="身高 (cm)">
              <InputNumber min={20} max={250} step={0.1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item name="scr" label="血清肌酐">
              <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="scr_unit" label="肌酐单位">
              <Select options={[...SCR_UNIT_OPTIONS]} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="albumin" label="白蛋白 (g/L)">
              <InputNumber min={0} max={60} step={0.1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item name="alt" label="ALT (U/L)">
              <InputNumber min={0} step={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="ast" label="AST (U/L)">
              <InputNumber min={0} step={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" plain>特殊状态</Divider>
        <Row gutter={16}>
          <Col xs={12} md={6}>
            <Form.Item name="ventilation" label="机械通气" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="inotropes" label="正性肌力药" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="ecmo" label="ECMO" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="ibuprofen" label="布洛芬" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        {isMobileShell ? (
          <Button type="primary" block loading={saving} onClick={handleSave}>
            保存修改
          </Button>
        ) : null}
      </Form>
    </div>
  );

  return (
    <div>
      {!isMobileShell ? (
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/cases')}
          style={{ marginBottom: 16, paddingLeft: 0 }}
        >
          返回病例列表
        </Button>
      ) : null}

      <div style={{ marginBottom: 16 }}>
        <Title level={4} style={{ marginBottom: 8 }}>
          病例详情
          <Tag color={caseData.status === 'active' ? 'green' : 'default'} style={{ marginLeft: 12 }}>
            {caseData.status === 'active' ? '进行中' : caseData.status}
          </Tag>
        </Title>
        <Text type="secondary">患者摘要、历史记录、TDM 优化和报告预览按单列顺序展示。</Text>
      </div>

      <Card
        title="患者摘要"
        extra={caseData.status === 'active' && !isMobileShell ? (
          <Button type="link" icon={<EditOutlined />} onClick={openEdit}>
            编辑患者信息
          </Button>
        ) : undefined}
        style={{ marginBottom: 16 }}
      >
        <Descriptions bordered size="small" column={isMobileShell ? 1 : { xs: 1, sm: 2, md: 3 }}>
          <Descriptions.Item label="患者编号">
            <Text strong>{caseData.patient_code || caseData.patient_id}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="目标药物">
            <Tag color="blue">{getDrugLabel(caseData.drug_name)}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="诊断">
            {caseData.diagnosis || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="体重 (kg)">
            <Text strong>{covariates.weight ?? '-'}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="校正胎龄 (周)">
            {covariates.age_pma ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label="PNA (天)">
            {covariates.age_pna ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label="胎龄 (周)">
            {covariates.gestational_age ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label="SCr">
            {covariates.scr != null ? `${covariates.scr} ${normalizeScrUnit(covariates.scr_unit || 'mg/dL')}` : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="身高 (cm)">
            {covariates.height ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label="白蛋白 (g/L)">
            {covariates.albumin ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label="ALT">
            {covariates.alt ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label="AST">
            {covariates.ast ?? '-'}
          </Descriptions.Item>
        </Descriptions>

        {(covariates.ventilation || covariates.inotropes || covariates.ecmo || covariates.ibuprofen) ? (
          <div style={{ marginTop: 16 }}>
            <Text type="secondary" style={{ marginRight: 8 }}>特殊状态</Text>
            <Space wrap>
              {covariates.ventilation ? <Tag color="orange">机械通气</Tag> : null}
              {covariates.inotropes ? <Tag color="red">正性肌力药</Tag> : null}
              {covariates.ecmo ? <Tag color="purple">ECMO</Tag> : null}
              {covariates.ibuprofen ? <Tag color="volcano">布洛芬</Tag> : null}
            </Space>
          </div>
        ) : null}

        {derived_metrics && Object.keys(derived_metrics).length > 0 ? (
          <>
            <Divider orientation="left" plain>派生指标</Divider>
            <Row gutter={[16, 12]}>
              {derived_metrics.bmi != null ? (
                <Col xs={12} md={6}>
                  <Text type="secondary">BMI</Text>
                  <div><Text strong>{derived_metrics.bmi.toFixed(2)} kg/m²</Text></div>
                </Col>
              ) : null}
              {derived_metrics.bsa != null ? (
                <Col xs={12} md={6}>
                  <Text type="secondary">BSA</Text>
                  <div><Text strong>{derived_metrics.bsa.toFixed(4)} m²</Text></div>
                </Col>
              ) : null}
              {derived_metrics.scr_mg_dl != null ? (
                <Col xs={12} md={6}>
                  <Text type="secondary">SCr</Text>
                  <div><Text strong>{derived_metrics.scr_mg_dl.toFixed(3)} mg/dL</Text></div>
                </Col>
              ) : null}
              {derived_metrics.crcl != null ? (
                <Col xs={12} md={6}>
                  <Text type="secondary">CrCl</Text>
                  <div>
                    <Text strong>{derived_metrics.crcl.toFixed(2)} mL/min/1.73m²</Text>
                    {derived_metrics.crcl_method ? (
                      <Text type="secondary" style={{ fontSize: 12 }}> ({derived_metrics.crcl_method})</Text>
                    ) : null}
                  </div>
                </Col>
              ) : null}
              {derived_metrics.pma_calculated != null ? (
                <Col xs={12} md={6}>
                  <Text type="secondary">校正胎龄</Text>
                  <div><Text strong>{derived_metrics.pma_calculated} 周</Text></div>
                </Col>
              ) : null}
            </Row>
          </>
        ) : null}

        <Divider />
        <Row gutter={[16, 8]}>
          <Col xs={24} md={12}>
            <Text type="secondary">创建时间：</Text>
            <Text>{new Date(caseData.created_at).toLocaleString('zh-CN')}</Text>
          </Col>
          <Col xs={24} md={12}>
            <Text type="secondary">更新时间：</Text>
            <Text>{new Date(caseData.updated_at).toLocaleString('zh-CN')}</Text>
          </Col>
        </Row>

        {caseData.status === 'active' && isMobileShell ? (
          <div style={{ marginTop: 16 }}>
            <Button type="primary" block icon={<EditOutlined />} onClick={openEdit}>
              编辑患者信息
            </Button>
          </div>
        ) : null}
      </Card>

      <div style={{ marginBottom: 16 }}>
        <CalculationHistory
          caseId={id!}
          drugName={caseData.drug_name}
          drugId={caseData.covariates?.drug_id}
          onLatestCalculationLoaded={handleLatestCalculationLoaded}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <TDMOptimizePanel
          caseId={id!}
          covariates={caseData.covariates}
          drugName={caseData.drug_name}
          replayDraft={tdmReplayDraft}
          onCalculated={(result: DoseResult) => {
            setReportResult(result);
            setTimeout(() => reportRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
          }}
        />
      </div>

      {reportResult?.calculation_id ? (
        <div ref={reportRef} style={{ marginBottom: 16 }}>
          <Card title="报告预览">
            <ReportPreview
              calculationId={reportResult.calculation_id}
              doseResult={reportResult}
              customInterpretation={reportResult.clinical_interpretation}
              patientCode={caseData.patient_code || String(caseData.patient_id)}
              drugName={caseData.drug_name}
            />
          </Card>
        </div>
      ) : null}

      {isMobileShell ? (
        <Drawer
          title="编辑患者信息"
          open={editOpen}
          onClose={() => setEditOpen(false)}
          width="100%"
          extra={<Button type="primary" loading={saving} onClick={handleSave}>保存</Button>}
        >
          {editFormContent}
        </Drawer>
      ) : (
        <Modal
          title="编辑患者信息"
          open={editOpen}
          onOk={handleSave}
          onCancel={() => setEditOpen(false)}
          confirmLoading={saving}
          width={720}
          okText="保存"
          cancelText="取消"
        >
          {editFormContent}
        </Modal>
      )}
    </div>
  );
};

export default CaseDetail;
